import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
  Announcement,
  Button,
  IconButton,
  NumeratorLogoSparkle,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@utilities';

import { createSummaryGPTFeedback } from '@api/instant_survey';

import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

const PreviewBanner = ({ generationError, onAcceptPreview, onReprompt }) => {
  const { jobId, surveyGoals } = useContext(SurveyContext);

  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const userId = useSelector((state) => state.user?.data?.id);
  const wasHelpful = selectedFeedback === true;
  const wasNotHelpful = selectedFeedback === false;

  useEffect(() => {
    setSelectedFeedback(null);
  }, [jobId]);

  const handleFeedback = async (feedback) => {
    if (isSavingFeedback || feedback === selectedFeedback) return;
    setIsSavingFeedback(true);
    try {
      const feedbackData = {
        feedback: feedback ? 'POSITIVE' : 'NEGATIVE',
        job: jobId,
        user_id: userId,
      };
      await createSummaryGPTFeedback({ data: feedbackData, jobId: jobId });
      setSelectedFeedback(feedback);
    } catch (error) {
      console.error('Failed to save feedback:', error);
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const footer = (
    <div className={styles['preview-banner-footer']}>
      <div className={styles['preview-banner-buttons']}>
        {generationError ? (
          <Button onClick={onReprompt} text="Try Again" variant="ai" />
        ) : (
          <>
            <Button onClick={onAcceptPreview} text="Insert Questions" variant="ai" />
            <Button onClick={onReprompt} text="Edit Prompt" variant="secondary" />
          </>
        )}
      </div>
      <div className={styles['preview-banner-feedback']}>
        <p>Was this helpful?</p>
        <IconButton
          className={wasHelpful ? styles['active'] : ''}
          onClick={() => handleFeedback(true)}
          tooltip="This was helpful"
        >
          <ThumbsUpIcon />
        </IconButton>
        <IconButton.Danger
          className={wasNotHelpful ? styles['active'] : ''}
          onClick={() => handleFeedback(false)}
          tooltip="This was not helpful"
        >
          <ThumbsDownIcon />
        </IconButton.Danger>
      </div>
    </div>
  );

  return (
    <Announcement
      className={styles['preview-banner-insert-announcement']}
      elevation={1}
      footer={footer}
      header={surveyGoals}
      icon={<NumeratorLogoSparkle className={styles['preview-banner-nmr-logo-sparkle']} />}
      text=""
      variant="info"
    />
  );
};

PreviewBanner.propTypes = {
  generationError: PropTypes.bool.isRequired,
  onAcceptPreview: PropTypes.func.isRequired,
  onReprompt: PropTypes.func.isRequired,
};

export default PreviewBanner;
